import type { ComponentType } from 'react';
import { servicePath, type ServiceRoute } from '@vnetwork/margarita-shell/service-route';
import type { KubernetesHost } from './host';
import { kubernetesPaths } from './paths';

type Loader<P = object> = () => Promise<{ default: ComponentType<P> }>;

/**
 * Managed Kubernetes: BOTH consoles, one module. Mounted by `ServicesPage` through `src/app`:
 *
 *   /services/kubernetes/v2/*  → K8sV2Page         the v2 console (Go backend, tenant-scoped)
 *   /services/kubernetes/*     → KubernetesPage    the v1 (legacy) console, service_uuid-scoped
 *
 * They stay two chunks, as they were: a customer on v1 does not download v2, and the other way
 * round. Nothing but these loaders is loaded with the route table.
 *
 * Declared beside v1 and ahead of it so the pair reads as one decision. Order is not what
 * picks the winner — React Router ranks `kubernetes/v2/*` above `kubernetes/*` because a
 * static segment outscores the splat — but a reader should not have to know that to see which
 * one `/v2` hits.
 *
 * Managed Kubernetes v2 (Go backend, tenant-scoped) runs ALONGSIDE the v1 s10015 console
 * rather than replacing it, behind `options.v2Enabled` (`APP_K8S_API_URL` is configured; the app
 * passes `!!env.k8sV2ApiUrl`).
 *
 * Plain data + lazy loaders: do not import a console here (it would join the entry chunk). The host is
 * lazy too: `load` resolves it and registers it before the console's chunk is even requested, so no
 * hook of the module can run before the host exists. `host.ts` is imported here, not at the top, so it
 * stays out of the entry chunk too. The promise is kept, so a second navigation does not resolve it
 * again; a failure is not kept.
 *
 * Promise chains, not `async`/`await`: this file is in the entry chunk and the build transpiles
 * `async` into a generator state machine.
 *
 * `loadActivateLanding` (the catalog detail page), `loadProjectClusters` and `loadClusterScopePicker`
 * (the Organizations console, through `k8sSlots`) load what other pages render outside these consoles,
 * behind the same registration, so no page or package imports this one.
 */
export function createKubernetesRoutes(
  loadHost: () => Promise<KubernetesHost>,
  options: { v2Enabled: boolean },
): {
  servicesRoutes: ServiceRoute[];
  loadActivateLanding: Loader<{ onBack?: () => void }>;
  loadProjectClusters: Loader<{ projectId: string }>;
  loadClusterScopePicker: Loader<{
    selected: string[];
    onChange: (ids: string[], hints: Record<string, { name: string; serviceId: string }>) => void;
  }>;
} {
  let hostReady: Promise<void> | undefined;
  const ready = () => {
    if (!hostReady) {
      hostReady = loadHost()
        .then((host) => import('./host').then((m) => m.registerKubernetesHost(host)))
        .catch((e) => {
          hostReady = undefined; // a failed chunk fetch must be retryable
          throw e;
        });
    }
    return hostReady;
  };
  return {
    servicesRoutes: [
      {
        path: servicePath(kubernetesPaths.v2.base, '*'),
        enabled: options.v2Enabled,
        load: () => ready().then(() => import('./v2/components/K8sV2Page')).then((m) => ({ default: m.K8sV2Page })),
      },
      {
        path: servicePath(kubernetesPaths.base, '*'),
        load: () => ready().then(() => import('./v1/components/KubernetesPage')).then((m) => ({ default: m.KubernetesPage })),
      },
    ],
    loadActivateLanding: () =>
      ready()
        .then(() => import('./shared/components/KubernetesActivateLanding'))
        .then((m) => ({ default: m.KubernetesActivateLanding })),
    loadProjectClusters: () =>
      ready()
        .then(() => import('./v2/components/ProjectClusters'))
        .then((m) => ({ default: m.ProjectClusters })),
    loadClusterScopePicker: () =>
      ready()
        .then(() => import('./v2/components/ClusterScopePicker'))
        .then((m) => ({ default: m.ClusterScopePicker })),
  };
}
